// F-W (2026-10-05): what an edit form shows until its record has loaded.
// Before this, a still-loading or failed load rendered the form with blank
// fields — indistinguishable from a record with no title / no parent. Now
// it's an explicit "Loading…" or "Couldn't load … Retry" panel.

import type { FC } from "react";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { ApiError } from "@shared/api/client";

export interface FormLoadStateProps {
  /** "zone" / "exhibit" — used in the messages. */
  noun: string;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onBack: () => void;
}

export function loadErrorMessage(noun: string, error: unknown): string {
  if (error instanceof ApiError && error.status === 404)
    return `This ${noun} doesn't exist or has been removed.`;
  if (error instanceof ApiError && error.status === 403)
    return `You don't have access to this ${noun}.`;
  return `Couldn't load this ${noun}. Check your connection and try again.`;
}

const FormLoadState: FC<FormLoadStateProps> = ({
  noun,
  isError,
  error,
  onRetry,
  onBack,
}) => (
  <div className="min-h-screen bg-background" data-testid="form-load-state">
    <div className="max-w-4xl mx-auto px-6 py-16 text-center space-y-4">
      {isError ? (
        <>
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-600" />
          <p className="text-lg font-medium" data-testid="form-load-error">
            {loadErrorMessage(noun, error)}
          </p>
          {error instanceof ApiError && error.message && (
            <p className="text-sm text-muted-foreground">{error.message}</p>
          )}
          <div className="flex justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              data-testid="form-load-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button
              type="button"
              onClick={onRetry}
              data-testid="form-load-retry"
            >
              Retry
            </Button>
          </div>
        </>
      ) : (
        <p
          className="flex items-center justify-center gap-2 text-muted-foreground"
          data-testid="form-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading {noun}…
        </p>
      )}
    </div>
  </div>
);

export default FormLoadState;
